import React from 'react';
import PropTypes from 'prop-types';

import BusComponent from '../BusComponent';
import ApplicationMode from '../../enums/ApplicationMode';
import ApplicationFrameType from '../../enums/ApplicationFrameType';

import UserAvatar from './UserAvatar';
import ShareButton from './ShareButton';
import LoginButton from './LoginButton';
import SignupButton from './SignupButton';
import HeaderLogo from './HeaderLogo';
import HelpCenterButton from './HelpCenterButton';
import MoreOptionsButtonCondensed from './MoreOptionsButtonCondensed';
import SaveButton from './SaveButton';
import SearchBoxPanel from '../searchBox/SearchBoxPanel';
import ReportEditorApplicationHeader from './ReportEditorApplicationHeader';
import UserLocations from '../userLocations/UserLocations';
import AppConfig from '../../appConfig';
import FindOpportunitiesButton from './findOpportunities/FindOpportunitiesButton';

/**
 * @typedef Props
 * @property {string} currentFrameType
 * @property {string} currentFrameLockMode
 * @property {boolean} isDingoProject
 * @property {boolean} canSave
 * @property {string} projectTitle
 * @property {boolean} isPublic
 * @property {import('../../objects/MapInstance').default[]} mapInstances
 *
 * @typedef State
 * @property {import('../../objects/UserInfo').default|{}} userInfo
 * @property {boolean} isFrameTypeSwitcherOpen
 * @property {string|undefined} applicationFrameType
 * @property {import('../../objects/MapInstance').default|undefined} reportMapInstance
 *
 * @extends {BusComponent<Props, State>}
 */
class ApplicationHeader extends BusComponent {
    constructor(props, context) {
        super(props, context);

        this.state = {
            userInfo: {},
            isFrameTypeSwitcherOpen: false,
            applicationFrameType: undefined,
            reportMapInstance: undefined,
            isFindOpportunitiesPopoverOpen: false,
        };

        this.bindGluBusEvents({
            USER_INFO_LOAD_SUCCESS: this.onUserInfo,
            LOG_OUT_SUCCESS: this.onUserInfo,
            APPLICATION_FRAME_TYPE_CHANGED: this.onApplicationFrameTypeChanged,
            REPORT_MODE_ACTIVATED: this.onReportModeActivated,
            REPORT_MODE_DEACTIVATED: this.onReportModeDeactivated,
        });
    }

    componentDidMount() {
        this.bus.once('USER_INFO_GET_SUCCESS', this.onUserInfo);
        this.emit('USER_INFO_GET_REQUEST', { source: this });
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
        this.bus.off('USER_INFO_GET_SUCCESS', this.onUserInfo);
    }

    /**
     * @param {object} param0
     * @param {import('../../objects/MapInstance').default} param0.mapInstance
     */
    onReportModeActivated = ({ mapInstance }) => {
        this.setState({
            reportMapInstance: mapInstance,
        });
    };

    onReportModeDeactivated = () => {
        this.setState({
            reportMapInstance: undefined,
        });
    };

    /**
     * @param {import('../../objects/UserInfo').default} userInfo
     * @param {BusComponent | undefined} source
     */
    onUserInfo = (userInfo, source) => {
        if (source && this !== source) return;
        this.setState({ userInfo });
    };

    /** @param {string} applicationFrameType */
    onApplicationFrameTypeChanged(applicationFrameType) {
        this.setState({
            applicationFrameType,
        });
    }

    toggleFindOpportunitiesPopover = () => {
        this.setState(prevState => ({
            isFindOpportunitiesPopoverOpen: !prevState.isFindOpportunitiesPopoverOpen,
        }));
    };

    onDataFilterClick = () => {
        const { mapInstances } = this.props;
        const { userInfo } = this.state;

        if (!userInfo || !mapInstances) return;

        this.emit('ENTER_DATA_FILTER_MODE', { mapInstanceId: mapInstances[0].id });
        this.toggleFindOpportunitiesPopover();
    };

    onFindFacilitiesClick = () => {
        const { mapInstances } = this.props;
        const { applicationFrameType } = this.state;

        if (!mapInstances) return;

        // Close filter areas by criteria frame if opened to open find facilities panel
        switch (true) {
            case applicationFrameType === ApplicationFrameType.DATA_FILTER_EDITOR:
                this.emit('EXIT_DATA_FILTER_MODE', {
                    mapInstance: mapInstances[0],
                    mapInstanceId: mapInstances[0].id,
                    newMode: 'SHOW_POINTS_PANEL',
                });

                // We need to add a simple timeout because the bus event isn't connected to a handler yet
                setTimeout(() => {
                    this.emit('SHOW_POINTS_PANEL', {
                        mapInstance: mapInstances[0],
                        mapInstanceId: mapInstances[0].id,
                    });
                }, 200);
                this.toggleFindOpportunitiesPopover();
                return;
            default:
                this.emit('SHOW_POINTS_PANEL', {
                    mapInstance: mapInstances[0],
                    mapInstanceId: mapInstances[0].id,
                });
                this.toggleFindOpportunitiesPopover();
        }
    };

    render() {
        const { isDingoProject, projectTitle, mapInstances, canSave } = this.props;

        const { applicationFrameType, userInfo, isFindOpportunitiesPopoverOpen } = this.state;

        const { isCondensedLayout, applicationMode } = this.context;

        switch (applicationFrameType) {
            // No ApplicationHeader in export mode
            case ApplicationFrameType.MAP_EXPORT:
                return null;
            // Reduced header in report editor mode
            case ApplicationFrameType.REPORT_EDITOR:
                return (
                    <ReportEditorApplicationHeader
                        reportMapInstance={this.state.reportMapInstance}
                        userInfo={userInfo}
                    />
                );
        }

        const isApplicationInViewMode = applicationMode === ApplicationMode.VIEW;

        // Show only limited header options in mobile view with the rest in more menu
        if (isCondensedLayout && isApplicationInViewMode) {
            return (
                <header className="application-header">
                    <div className="section section--full-width">
                        <HeaderLogo />
                        <SearchBoxPanel mapInstanceIds={mapInstances.map(m => m.id)} />
                    </div>
                    <div className="section flex-fixed margin-right-10">
                        <MoreOptionsButtonCondensed
                            projectTitle={projectTitle}
                            userInfo={userInfo}
                        />
                    </div>
                </header>
            );
        }

        const showSaveProjectControl = isDingoProject && userInfo.isLogged;

        return (
            <header className="application-header">
                <div className="section section--full-width">
                    <HeaderLogo />
                    {userInfo.isLogged && (
                        <SearchBoxPanel mapInstanceIds={mapInstances.map(m => m.id)} />
                    )}
                    {userInfo.isLogged && (
                        <UserLocations mapInstanceIds={mapInstances.map(m => m.id)} />
                    )}
                    {userInfo.isLogged && (
                        <FindOpportunitiesButton
                            onFindAreasClick={this.onDataFilterClick}
                            onFindFacilitiesClick={this.onFindFacilitiesClick}
                            togglePopover={this.toggleFindOpportunitiesPopover}
                            isPopoverVisible={isFindOpportunitiesPopoverOpen}
                        />
                    )}
                </div>
                <div className="section flex-fixed">
                    {AppConfig.constants.headerItems.map(headerItem => {
                        switch (headerItem.type) {
                            case 'SHARE':
                                return <ShareButton key="share" />;
                            case 'HELP_CENTER':
                                return (
                                    userInfo.isLogged && (
                                        <HelpCenterButton
                                            key="helpCenter"
                                            isApplicationInViewMode={isApplicationInViewMode}
                                            userInfo={userInfo}
                                        />
                                    )
                                );
                            case 'SAVE': {
                                return (
                                    showSaveProjectControl && (
                                        <SaveButton
                                            key="save"
                                            projectTitle={projectTitle}
                                            isEdit={canSave}
                                            buttonVariant="raised"
                                        />
                                    )
                                );
                            }
                            default:
                                return <div />;
                        }
                    })}
                    <span
                        style={{
                            marginLeft: 14,
                            height: 22,
                            width: 1,
                            background: 'rgba(0, 0, 0, 0.13)',
                        }}
                    />
                    <div className="user-info-wrapper">
                        {userInfo.isLogged && <UserAvatar userInfo={userInfo} />}
                        {!userInfo.isLogged && <LoginButton />}
                        {!userInfo.isLogged && <SignupButton />}
                    </div>
                </div>
            </header>
        );
    }
}

ApplicationHeader.propTypes = {
    currentFrameType: PropTypes.string.isRequired,
    currentFrameLockMode: PropTypes.string.isRequired,
    mapInstances: PropTypes.array.isRequired,
    projectTitle: PropTypes.string,
    isDingoProject: PropTypes.bool,
    canSave: PropTypes.bool,
    isPublic: PropTypes.bool,
};

ApplicationHeader.defaultProps = {
    projectTitle: '',
    isPublic: undefined,
    isDingoProject: true,
    canSave: false,
};

export default ApplicationHeader;
